/**
 * Sun position and shadow projection.
 *
 * This is a direct port of the 2023 implementation in
 * legacy/js/nycTreesSingleShadow.js. The math is the scientific core of the
 * project and is preserved exactly -- including the empirical degree-to-metre
 * constants -- so that results remain comparable with the published work.
 * Only the surrounding structure (types, pure functions, no globals) changed.
 */
import * as SunCalc from 'suncalc';

/**
 * Degrees of longitude / latitude per metre at NYC's latitude, from
 * http://www.csgnetwork.com/degreelenllavcalc.html. Carried over verbatim from
 * the 2023 code; changing these would shift every shadow.
 */
const METRES_PER_DEGREE_LON = 84540.7;
const METRES_PER_DEGREE_LAT = 111047.7;

/** Feet per metre, as used by the original height conversions. */
export const FEET_PER_METRE = 3.28;

export interface SunVector {
  /** Azimuth in degrees. */
  azimuth: number;
  /** Altitude above the horizon in degrees. */
  altitude: number;
  sinAz: number;
  cosAz: number;
  tanAmp: number;
  /** True when the sun is below the horizon and shadows are undefined. */
  belowHorizon: boolean;
}

export function getSunVector(date: Date, lat: number, lon: number): SunVector {
  const { azimuth, altitude } = SunCalc.getPosition(date, lat, lon);
  const az = (azimuth * 180) / Math.PI;
  const amp = (altitude * 180) / Math.PI;

  return {
    azimuth: az,
    altitude: amp,
    sinAz: Math.sin((az * Math.PI) / 180),
    cosAz: Math.cos((az * Math.PI) / 180),
    // Negated altitude, matching the original: tanAmp is the ratio used to
    // trade height for ground distance when casting a point downward.
    tanAmp: Math.tan((-amp * Math.PI) / 180),
    belowHorizon: amp <= 0,
  };
}

/**
 * Projects a point at height `z` (metres) onto the ground along the sun ray.
 * Returns [lon, lat] of where its shadow falls.
 */
export function projectToGround(
  lon: number,
  lat: number,
  z: number,
  sun: SunVector,
): [number, number] {
  return [
    lon - ((z / sun.tanAmp) * sun.sinAz) / METRES_PER_DEGREE_LON,
    lat - ((z / sun.tanAmp) * sun.cosAz) / METRES_PER_DEGREE_LAT,
  ];
}

export type PointClass = 'shaded' | 'shading' | 'other';

/**
 * Classifies a canopy point against the ground shadows cast by nearby
 * buildings:
 *
 *   shading - the point's own shadow lands on a building footprint, i.e. the
 *             canopy is shading a building
 *   shaded  - the point is itself inside a building's shadow volume
 *   other   - the canopy is shading open ground
 *
 * `hulls` are the ground-projected convex hulls of the selected buildings.
 */
export function classifyPoint(
  lon: number,
  lat: number,
  z: number,
  hulls: Array<Array<[number, number]>>,
  sun: SunVector,
): PointClass {
  const [groundLon, groundLat] = projectToGround(lon, lat, z, sun);

  let isShading = false;
  let isShaded = false;

  for (const hull of hulls) {
    const insideSky = pointInPolygon([lon, lat], hull);
    const insideGround = pointInPolygon([groundLon, groundLat], hull);

    if (insideGround && !insideSky) {
      isShading = true;
    } else if (insideGround && insideSky) {
      isShaded = true;
    }
  }

  if (isShaded) return 'shaded';
  if (isShading) return 'shading';
  return 'other';
}

/**
 * Ray-casting point-in-polygon.
 * https://wrf.ecse.rpi.edu/Research/Short_Notes/pnpoly.html
 */
export function pointInPolygon(point: [number, number], vs: Array<[number, number]>): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const [xi, yi] = vs[i];
    const [xj, yj] = vs[j];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Andrew's monotone chain convex hull.
 * https://www.nayuki.io/page/convex-hull-algorithm
 *
 * NOTE: the 2023 version called Array.prototype.sort() with no comparator on an
 * array of [x, y] pairs, which sorts by string coercion and can order points
 * incorrectly (e.g. "-73.9" < "-73.85" lexicographically). This port sorts
 * numerically by x then y, which is what the algorithm requires.
 */
export function convexHull(points: Array<[number, number]>): Array<[number, number]> {
  if (points.length <= 1) return [...points];

  const sorted = [...points].sort((a, b) => (a[0] !== b[0] ? a[0] - b[0] : a[1] - b[1]));

  const cross = (
    o: [number, number],
    a: [number, number],
    b: [number, number],
  ): number => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const lower: Array<[number, number]> = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper: Array<[number, number]> = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Ground shadow of a building: the convex hull of its footprint together with
 * that footprint projected along the sun ray. `heightRoofFeet` matches the
 * `heightroof` attribute in the building tiles.
 */
export function buildingShadowHull(
  footprint: Array<[number, number]>,
  heightRoofFeet: number,
  sun: SunVector,
): Array<[number, number]> {
  const z = heightRoofFeet / FEET_PER_METRE;
  const points: Array<[number, number]> = [];

  for (const [lon, lat] of footprint) {
    points.push([lon, lat]);
    points.push(projectToGround(lon, lat, z, sun));
  }

  return convexHull(points);
}

/**
 * Sunrise/sunset in fractional local hours, used to bound the hour slider.
 *
 * SunCalc returns null at latitudes with polar day/night. That cannot happen at
 * NYC's latitude, but rather than assert we fall back to a plain 07:00-18:00
 * window so the control stays usable if this is ever reused elsewhere.
 */
export function daylightRange(date: Date, lat: number, lon: number): { min: number; max: number } {
  const times = SunCalc.getTimes(date, lat, lon);
  const toHours = (d: Date) => d.getHours() + d.getMinutes() / 60;

  const sunrise = times.sunrise instanceof Date && !isNaN(times.sunrise.getTime())
    ? toHours(times.sunrise) + 0.5
    : 7;
  const sunset = times.sunset instanceof Date && !isNaN(times.sunset.getTime())
    ? toHours(times.sunset) - 0.5
    : 18;

  return sunset > sunrise ? { min: sunrise, max: sunset } : { min: 7, max: 18 };
}

/** Builds a local Date from a day-of-year (1-365) and fractional hour. */
export function dateFromDayHour(dayOfYear: number, hour: number, year = 2022): Date {
  const date = new Date(year, 0, 1, 0, 0, 0, 0);
  date.setDate(date.getDate() + dayOfYear - 1);
  const wholeHour = Math.floor(hour);
  const minutes = Math.round((hour - wholeHour) * 60);
  date.setHours(wholeHour, minutes, 0, 0);
  return date;
}
